// ============================================================================
// useAdminTeamOverview.js — Batch-fetch all team member data for admin overview
// ============================================================================
// Fetches from: user_profiles, user_education, user_employers,
//   user_salary_history, user_performance_reviews, user_evaluation_reports
// All in a single parallel batch for efficient overview rendering.
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

// ── Role history normalizer ────────────────────────────────────────────────
function normalizeRoleHistory(row) {
  return {
    id: row.id, userId: row.user_id,
    effectiveFrom: row.effective_from,
    effectiveTo: row.effective_to || null,
    jobRole: row.job_role || '', jobLevel: row.job_level ?? 0,
    officeLocation: row.office_location || null,
  };
}

// ── Normalizers (mirrors useTeamMemberProfile.js) ──────────────────────────

function normalizeProfile(row) {
  if (!row) return null;
  return {
    id: row.id,
    userId: row.user_id,
    dob: row.dob || null,
    homeCity: row.home_city || '',
    designation: row.designation || '',
    personalityTestResult: row.personality_test_result || '',
    expertise: row.expertise || [],
    profilePhotoRound: row.profile_photo_round || '',
    currentJobRole: row.current_job_role || '',
    currentJobLevel: row.current_job_level ?? null,
    joiningDate: row.joining_date || null,
    graduationDate: row.graduation_date || null,
    experienceGapYears: row.experience_gap_years ?? null,
    planningMasters: row.planning_masters || '',
    officeLocation: row.office_location || 'NL',
    profileIntro: row.profile_intro || '',
    phoneMobile: row.phone_mobile || '',
    emailOffice: row.email_office || '',
    emailPersonal: row.email_personal || '',
  };
}

function normalizeEducation(row) {
  return {
    id: row.id, userId: row.user_id,
    type: row.type || '', institution: row.institution || '',
    graduationDate: row.graduation_date || null,
  };
}

function normalizeEmployer(row) {
  return {
    id: row.id, userId: row.user_id,
    name: row.name || '', startDate: row.start_date || null,
    endDate: row.end_date || null, role: row.role || '',
  };
}

function normalizeSalaryHistory(row) {
  return {
    id: row.id, userId: row.user_id,
    effectiveDate: row.effective_date, salary: row.salary,
  };
}

function normalizePerformanceReview(row) {
  return {
    id: row.id, userId: row.user_id,
    reviewDate: row.review_date || null,
    year: row.year ?? null,
    overallRating: row.overall_rating ?? null,
    sectionF: row.section_f || {},
  };
}

function normalizeEvaluationReport(row) {
  return {
    id: row.id, userId: row.user_id,
    evaluationDate: row.evaluation_date || null,
    year: row.year ?? null,
    performanceRating: row.performance_rating ?? null,
    averageRating: row.average_rating ?? null,
  };
}

// ── Denormalizer for profile writes ────────────────────────────────────────

function denormalizeProfileField(field, value) {
  const map = {
    dob: 'dob',
    homeCity: 'home_city',
    designation: 'designation',
    personalityTestResult: 'personality_test_result',
    expertise: 'expertise',
    joiningDate: 'joining_date',
    graduationDate: 'graduation_date',
    planningMasters: 'planning_masters',
    phoneMobile: 'phone_mobile',
    emailOffice: 'email_office',
    emailPersonal: 'email_personal',
    officeLocation: 'office_location',
  };
  const dbCol = map[field];
  if (!dbCol) return null;
  return { [dbCol]: value || null };
}

// ============================================================================
// HOOK
// ============================================================================
export function useAdminTeamOverview() {
  const [profiles, setProfiles] = useState([]);
  const [education, setEducation] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [salaryHistory, setSalaryHistory] = useState([]);
  const [performanceReviews, setPerformanceReviews] = useState([]);
  const [evaluationReports, setEvaluationReports] = useState([]);
  const [roleHistory, setRoleHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [profileRes, eduRes, empRes, salaryRes, reviewRes, evalRes, roleHistRes] = await Promise.all([
        supabase.from('user_profiles').select('*'),
        supabase.from('user_education').select('id, user_id, type, institution, graduation_date'),
        supabase.from('user_employers').select('id, user_id, name, start_date, end_date, role').order('start_date', { ascending: false }),
        supabase.from('user_salary_history').select('id, user_id, effective_date, salary').order('effective_date', { ascending: false }),
        supabase.from('user_performance_reviews').select('id, user_id, review_date, year, overall_rating, section_f').order('review_date', { ascending: false }),
        supabase.from('user_evaluation_reports').select('id, user_id, evaluation_date, year, performance_rating, average_rating').order('evaluation_date', { ascending: false }),
        supabase.from('user_role_history').select('id, user_id, effective_from, effective_to, job_role, job_level, office_location').order('effective_from', { ascending: false }),
      ]);

      if (profileRes.error) throw profileRes.error;
      setProfiles((profileRes.data || []).map(normalizeProfile));
      setEducation((eduRes.data || []).map(normalizeEducation));
      setEmployers((empRes.data || []).map(normalizeEmployer));
      setSalaryHistory((salaryRes.data || []).map(normalizeSalaryHistory));
      setPerformanceReviews((reviewRes.data || []).map(normalizePerformanceReview));
      setEvaluationReports((evalRes.data || []).map(normalizeEvaluationReport));
      setRoleHistory((roleHistRes.data || []).map(normalizeRoleHistory));
    } catch (err) {
      console.error('[useAdminTeamOverview] fetchAll error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Update a single profile field ──────────────────────────────────────
  const updateProfileField = async (userId, field, value) => {
    const dbUpdate = denormalizeProfileField(field, value);
    if (!dbUpdate) return { success: false, error: 'Unknown field' };
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');
      const { error: err } = await supabase
        .from('user_profiles')
        .upsert({ user_id: userId, ...dbUpdate }, { onConflict: 'user_id' });
      if (err) throw err;
      // Update local state
      setProfiles(prev => {
        const exists = prev.some(p => p.userId === userId);
        if (exists) {
          return prev.map(p => p.userId === userId ? { ...p, [field]: value || '' } : p);
        }
        return [...prev, { userId, [field]: value || '' }];
      });
      return { success: true };
    } catch (err) {
      console.error('[useAdminTeamOverview] updateProfileField error:', err);
      return { success: false, error: err.message };
    }
  };

  // ── Update evaluation report performance rating ─────────────────────
  const updateEvalRating = async (evalId, rating) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');
      const { error: err } = await supabase
        .from('user_evaluation_reports')
        .update({ performance_rating: rating })
        .eq('id', evalId);
      if (err) throw err;
      setEvaluationReports(prev => prev.map(e =>
        e.id === evalId ? { ...e, performanceRating: rating } : e
      ));
      return { success: true };
    } catch (err) {
      console.error('[useAdminTeamOverview] updateEvalRating error:', err);
      return { success: false, error: err.message };
    }
  };

  // ── Helpers: group data by userId ────────────────────────────────────
  const getProfileForUser = useCallback((userId) =>
    profiles.find(p => p.userId === userId) || null
  , [profiles]);

  const getEducationForUser = useCallback((userId) =>
    education.filter(e => e.userId === userId)
  , [education]);

  const getEmployersForUser = useCallback((userId) =>
    employers.filter(e => e.userId === userId)
  , [employers]);

  const getSalaryHistoryForUser = useCallback((userId) =>
    salaryHistory.filter(s => s.userId === userId)
  , [salaryHistory]);

  const getReviewsForUser = useCallback((userId) =>
    performanceReviews.filter(r => r.userId === userId)
  , [performanceReviews]);

  const getEvalReportsForUser = useCallback((userId) =>
    evaluationReports.filter(e => e.userId === userId)
  , [evaluationReports]);

  const getRoleHistoryForUser = useCallback((userId) =>
    roleHistory.filter(r => r.userId === userId)
  , [roleHistory]);

  // ── Update promotion readiness in section_f ─────────────────────
  const updatePromotionReadiness = async (reviewId, value) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) throw new Error('Please sign in');
      // First fetch the current section_f
      const { data: current, error: fetchErr } = await supabase
        .from('user_performance_reviews')
        .select('section_f')
        .eq('id', reviewId)
        .single();
      if (fetchErr) throw fetchErr;
      const updatedSectionF = { ...(current?.section_f || {}), promotionReadiness: value };
      const { error: err } = await supabase
        .from('user_performance_reviews')
        .update({ section_f: updatedSectionF })
        .eq('id', reviewId);
      if (err) throw err;
      setPerformanceReviews(prev => prev.map(r =>
        r.id === reviewId ? { ...r, sectionF: updatedSectionF } : r
      ));
      return { success: true };
    } catch (err) {
      console.error('[useAdminTeamOverview] updatePromotionReadiness error:', err);
      return { success: false, error: err.message };
    }
  };

  return {
    loading, error, refresh: fetchAll,
    profiles, education, employers, salaryHistory, performanceReviews, evaluationReports, roleHistory,
    getProfileForUser, getEducationForUser, getEmployersForUser,
    getSalaryHistoryForUser, getReviewsForUser, getEvalReportsForUser, getRoleHistoryForUser,
    updateProfileField,
    updateEvalRating,
    updatePromotionReadiness,
  };
}
